/**
 * Projects React Query Hooks — v3 Project Multi-Tenancy
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listOrgProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  setProjectGoals,
  discoverProjectMatches,
  getProjectMatches,
  getProjectMeasures,
} from '../services/projects';

/** List all projects for an org BN */
export const useOrgProjects = (bn, filters = {}) =>
  useQuery({
    queryKey: ['projects', 'org', bn, filters],
    queryFn:  () => listOrgProjects(bn, filters),
    enabled:  !!bn,
    staleTime: 30_000,
  });

/** Get a single project */
export const useProject = (projectId) =>
  useQuery({
    queryKey: ['projects', projectId],
    queryFn:  () => getProject(projectId),
    enabled:  !!projectId,
  });

/** Create a new project */
export const useCreateProject = (bn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => createProject(bn, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', 'org', bn] }),
  });
};

/** Update a project (fields + optional goals in one call) */
export const useUpdateProject = (bn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, updates }) => {
      // Split goal fields from project fields
      const goalKeys = ['l2_focus_area', 'l3_methods', 'l4_tools'];
      const goals    = {};
      const fields   = {};
      for (const [k, v] of Object.entries(updates)) {
        if (goalKeys.includes(k)) goals[k] = v;
        else fields[k] = v;
      }
      // PATCH project fields first
      const result = await updateProject(projectId, fields);
      // POST goals if any were provided
      if (Object.keys(goals).length > 0) {
        await setProjectGoals(projectId, { ...goals, manually_reviewed: true });
      }
      return result;
    },
    onSuccess: (_data, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['projects', 'org', bn] });
      qc.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
};

/** Delete a project */
export const useDeleteProject = (bn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId) => deleteProject(projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', 'org', bn] }),
  });
};

/** Set/update project goals */
export const useSetProjectGoals = (bn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, goals }) => setProjectGoals(projectId, goals),
    onSuccess: (_data, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['projects', 'org', bn] });
      qc.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
};

/** Get project match run results */
export const useProjectMatches = (projectId) =>
  useQuery({
    queryKey: ['project-matches', projectId],
    queryFn:  () => getProjectMatches(projectId),
    enabled:  !!projectId,
    staleTime: 60_000,
  });

/** Get project M1-M8 measures */
export const useProjectMeasures = (projectId) =>
  useQuery({
    queryKey: ['project-measures', projectId],
    queryFn:  () => getProjectMeasures(projectId),
    enabled:  !!projectId,
    staleTime: 60_000,
  });

/** Discover (run match) for a project */
export const useDiscoverProject = (bn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId) => discoverProjectMatches(projectId),
    onSuccess: (_data, projectId) => {
      qc.invalidateQueries({ queryKey: ['project-matches', projectId] });
      qc.invalidateQueries({ queryKey: ['project-measures', projectId] });
      qc.invalidateQueries({ queryKey: ['projects', 'org', bn] });
    },
  });
};
